import React, { useEffect } from 'react';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useTrigger } from '@atlassian/post-office-context';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { asyncNumberFormattingFlagEntryPoint } from './ui/entrypoint.tsx';
import { useFetchCriteria } from './services/useFetchCriteria.tsx';
import { TRIGGER_ID } from './common/constants.tsx';
import { emitMessageEvent } from './common/utils/emitMessageEvent.tsx';

type Props = {
	projectId: string;
	projectKey: string;
};

const NumberFormattingChangeboarding = ({ projectId, projectKey }: Props) => {
	const { isActive } = useTrigger(TRIGGER_ID);
	const data = useFetchCriteria(
		projectId,
		projectKey,
		isActive && fg('in_context_config_sidebar_changeboarding'),
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncNumberFormattingFlagEntryPoint,
		{},
	);

	const meetsCriteria =
		data !== undefined &&
		data?.hasExistingNumberFieldWithFormatStyle !== true &&
		data?.isProjectAdmin &&
		data?.isTMP;

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (!meetsCriteria) {
			emitMessageEvent({ action: 'filter' });
			return;
		}

		if (isActive && !fg('in_context_config_sidebar_changeboarding')) {
			emitMessageEvent({ action: 'snooze' });
			return;
		}

		entryPointActions.load();
	}, [entryPointActions, meetsCriteria, data, isActive]);

	if (!meetsCriteria || !isActive) return null;

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="number-currency-formatting-changeboarding-flag"
			packageName="@jira/number-currency-formatting-changeboarding-flag"
			teamName="a4t-tanuki"
			fallback={null}
			errorFallback="unmount"
			runtimeProps={{ projectKey, projectType: data.projectType ?? undefined }}
		/>
	);
};

export const NumberFormattingChangeboardingWrapper = ({
	projectId,
	projectKey,
}: Props): React.JSX.Element => {
	return (
		<JSErrorBoundary
			id="public-forms-changeboarding-modal"
			packageName="@atlassian/jira-public-forms-changeboarding-modal"
			teamName="a4t-tanuki"
			fallback="unmount"
		>
			<ApolloClientProvider client={client}>
				<NumberFormattingChangeboarding projectId={projectId} projectKey={projectKey} />
			</ApolloClientProvider>
		</JSErrorBoundary>
	);
};
